'use client';

import clsx from 'clsx';
import { Image } from '@/components/raven/Image';
import { type FC, useState, useEffect } from 'react';
import { Overlay } from '@/components/raven/Overlay';
import { Button } from '@/components/raven/ui/Button';
import { useOverlayContext } from '@/context/OverlayContext';
import type { InlineGalleryOverlayProps } from '@/types/inline-gallery';
import {
	useControls,
	TransformWrapper,
	TransformComponent,
} from 'react-zoom-pan-pinch';
import {
	Caption,
	Attribution,
} from '@/components/raven/Blocks/InlineGalleryBlock/Metadata';
import {
	Close,
	ZoomIn,
	ZoomOut,
	ArrowLeft,
	ArrowRight,
} from '@themaven-net/raven-assets';

import styles from '../styles.module.css';

interface ControlsProps {
	handleZoomIn: () => void;
	handleZoomOut: () => void;
	zoomed: boolean;
}

const Controls: FC<ControlsProps> = ({
	handleZoomIn,
	handleZoomOut,
	zoomed,
}) => {
	const { zoomIn, zoomOut } = useControls();

	return (
		<div className={styles.gutterButtons}>
			<Button
				aria-label="Zoom in image"
				className={clsx(styles.button, {
					[styles.zoomed]: zoomed,
				})}
				disabled={zoomed}
				onClick={() => {
					zoomIn(1);
					handleZoomIn();
				}}
				variation="icon-large"
			>
				<ZoomIn />
			</Button>
			<Button
				aria-label="Zoom out image"
				className={clsx(styles.button, {
					[styles.zoomed]: zoomed,
				})}
				disabled={!zoomed}
				onClick={() => {
					zoomOut(1);
					handleZoomOut();
				}}
				variation="icon-large"
			>
				<ZoomOut />
			</Button>
		</div>
	);
};

export const InlineGalleryOverlay: FC<InlineGalleryOverlayProps> = ({
	galleryId,
	setSlideIdx,
	slideIdx,
	slides,
}) => {
	const { closeVariant } = useOverlayContext();
	const [zoomed, setZoomed] = useState(false);
	const [showText, setShowText] = useState(false);
	const slide = slides[slideIdx];

	useEffect(() => {
		if (!slide) {
			setSlideIdx(0);
			setZoomed(false);
			closeVariant(galleryId);
		}
	}, [slide, setSlideIdx, closeVariant, galleryId]);

	if (!slide) {
		return null;
	}

	const { description, image, title } = slide;
	const {
		alt,
		attribution,
		attribution_url: attributionUrl,
		caption,
		height,
		url,
		width,
	} = image;

	const handleClose = () => {
		setZoomed(false);
		setShowText(false);
		closeVariant(galleryId);

		// Reset scrolling of content behind modal when modal is closing.
		document.body.classList.remove('no-scroll');
	};
	const handleZoomIn = () => setZoomed(true);
	const handleZoomOut = () => setZoomed(false);
	const handlePrevSlide = () => {
		setZoomed(false);
		setShowText(false);
		setSlideIdx(Math.max(0, slideIdx - 1));
	};
	const handleNextSlide = () => {
		setZoomed(false);
		setShowText(false);
		setSlideIdx(Math.min(slides.length - 1, slideIdx + 1));
	};
	const handleSeeMoreClick = () => setShowText(true);
	const handleSeeLessClick = () => setShowText(false);

	const isPortrait = height > width;
	const truncateDescription = description && description.length > 50;

	return (
		<Overlay
			className={clsx('full-width', styles.overlay)}
			disableCloseButton
			disableTransition
			onClose={handleClose}
			variant={galleryId}
		>
			<div className={styles.desktopView}>
				<div className={styles.modal}>
					<div
						className={clsx(styles.leftPanel, {
							[styles.isPortrait]: isPortrait,
						})}
					>
						<div className={clsx(styles.gutter, styles.topGutter)} />
						<TransformWrapper key={slideIdx} maxScale={2}>
							<div
								className={clsx(styles.modalImageContainer, {
									[styles.isPortrait]: isPortrait,
								})}
							>
								<TransformComponent>
									<Image
										alt={alt ?? ''}
										className={clsx(styles.modalImage)}
										height={Number(height)}
										src={url}
										width={Number(width)}
									/>
								</TransformComponent>
							</div>
							<div className={clsx(styles.gutter, styles.bottomGutter)}>
								<Controls
									handleZoomIn={handleZoomIn}
									handleZoomOut={handleZoomOut}
									zoomed={zoomed}
								/>
							</div>
						</TransformWrapper>
					</div>

					<div className={styles.rightPanel}>
						<div className={styles.rightPanelHeader}>
							{title && <h3 className={styles.modalTitle}>{title}</h3>}
							<Button
								aria-label="Close modal"
								className={clsx(styles.button, styles.close)}
								onClick={handleClose}
								variation="icon-large"
							>
								<Close />
							</Button>
						</div>

						<div className={styles.rightPanelBody}>
							<div className={styles.description}>
								{description && <p>{description}</p>}
							</div>
							<div className={styles.metadata}>
								<Caption caption={caption} />
								<Attribution
									sourceName={attribution}
									sourceUrl={attributionUrl}
								/>
							</div>
						</div>

						<div className={styles.rightPanelFooter}>
							<div className={styles.footerButtons}>
								<span className={styles.slideCounter}>
									{slideIdx + 1} / {slides.length}
								</span>
								<Button
									aria-label="Previous slide"
									className={styles.button}
									disabled={slideIdx === 0}
									onClick={handlePrevSlide}
									variation="icon-large"
								>
									<ArrowLeft />
								</Button>
								<Button
									aria-label="Next slide"
									className={styles.button}
									disabled={slideIdx >= slides.length - 1}
									onClick={handleNextSlide}
									variation="icon-large"
								>
									<ArrowRight />
								</Button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className={styles.mobileView}>
				<div className={styles.modal}>
					<div className={styles.header}>
						{title && <h3>{title}</h3>}
						<Button
							aria-label="Close modal"
							className={clsx(styles.button, styles.close)}
							onClick={handleClose}
							variation="icon-large"
						>
							<Close />
						</Button>
					</div>

					<div
						className={clsx(styles.mobileImageContainer, {
							[styles.isPortrait]: isPortrait,
						})}
					>
						<TransformWrapper key={slideIdx} maxScale={2}>
							<TransformComponent>
								<Image
									alt={alt ?? ''}
									className={clsx(styles.modalImage)}
									height={Number(height)}
									src={url}
									width={Number(width)}
								/>
							</TransformComponent>
						</TransformWrapper>
					</div>
					<div className={styles.footer}>
						<div
							className={clsx(styles.metadata, {
								[styles.overlayMetadata]: showText,
							})}
						>
							{description && (
								<div className={styles.description}>
									<p
										aria-expanded={showText}
										className={clsx({
											[styles.clampedText]: truncateDescription && !showText,
										})}
									>
										{description}
									</p>
									{truncateDescription && !showText && (
										<Button
											aria-expanded={showText}
											className={styles.seeButton}
											onClick={handleSeeMoreClick}
										>
											See More
										</Button>
									)}
								</div>
							)}
							{(!truncateDescription || showText) && (
								<>
									<Caption caption={caption} />
									<Attribution
										sourceName={attribution}
										sourceUrl={attributionUrl}
									/>
								</>
							)}
							{showText && (
								<Button
									className={clsx(styles.button, styles.seeButton)}
									onClick={handleSeeLessClick}
								>
									See Less
								</Button>
							)}
						</div>
						<div className={styles.footerButtons}>
							{slideIdx + 1} / {slides.length}
							<Button
								aria-label="Previous slide"
								className={clsx(styles.button, styles.arrow)}
								disabled={slideIdx === 0}
								onClick={handlePrevSlide}
								variation="icon-large"
							>
								<ArrowLeft />
							</Button>
							<Button
								aria-label="Next slide"
								className={clsx(styles.button, styles.arrow)}
								disabled={slideIdx >= slides.length - 1}
								onClick={handleNextSlide}
								variation="icon-large"
							>
								<ArrowRight />
							</Button>
						</div>
					</div>
				</div>
			</div>
		</Overlay>
	);
};
